import { Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PixelButton } from '../ui/PixelButton';
import type { ButtonTone } from '../ui/PixelButton';
import { PixelText } from '../ui/PixelText';
import { PixelPanel } from '../ui/PixelPanel';
import { useAudio } from '../audio/useAudio';
import { usePlayerStore } from '../store/player';

type NavCard = { labelKey: string; to: string; tone: ButtonTone; icon: string };

const NAV_CARDS: NavCard[] = [
  { icon: '⚔', labelKey: 'campaign.title',  to: '/campaign', tone: 'pink'   },
  { icon: '☀', labelKey: 'daily.title',     to: '/daily',    tone: 'yellow' },
  { icon: '🏆', labelKey: 'ladder.title',   to: '/ladder',   tone: 'cyan'   },
  { icon: '★', labelKey: 'roster.title',    to: '/roster',   tone: 'green'  },
  { icon: '✦', labelKey: 'gacha.title',     to: '/gacha',    tone: 'orange' },
  { icon: '♥', labelKey: 'friends.title',   to: '/friends',  tone: 'cyan'   },
  { icon: '⚙', labelKey: 'settings.title', to: '/settings', tone: 'white'  },
];

const GROUP_BACKDROPS = ['bts', 'blackpink', 'aespa', 'newjeans', 'stray_kids', 'twice'] as const;
const TUTORIAL_SPRITE_IDS = ['jungkook', 'karina', 'lisa', 'bangchan', 'minji'];

function pickBackdrop(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return GROUP_BACKDROPS[hash % GROUP_BACKDROPS.length]!;
}

function SignedInDashboard() {
  const { t } = useTranslation();
  const me = usePlayerStore(s => s.me)!;
  const backdrop = useMemo(() => pickBackdrop(me.displayName), [me.displayName]);

  return (
    <main className="h-screen flex flex-col items-center justify-center gap-4 p-6 relative overflow-hidden">
      {/* Backdrop */}
      <img
        src={`/stanwars/sprites/groups/${backdrop}.png`}
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none"
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          objectFit: 'fill',
          imageRendering: 'pixelated',
          opacity: 0.22,
        }}
      />

      <PixelText size="3xl" color="pink">{t('home.title')}</PixelText>

      {/* Player header */}
      <PixelPanel className="w-full max-w-lg text-center relative">
        <PixelText size="lg" color="cyan">{me.displayName}</PixelText>
        <div className="mt-1 flex justify-center gap-4 flex-wrap">
          <PixelText size="sm" color="yellow">{t('home.code')}: {me.friendCode}</PixelText>
          <PixelText size="sm" color="green">{me.tier}</PixelText>
        </div>
      </PixelPanel>

      {/* Currency row */}
      <div className="flex gap-6 flex-wrap justify-center relative">
        <PixelText size="sm" color="yellow">{t('home.gold')}: {me.currencies.gold}</PixelText>
        <PixelText size="sm" color="cyan">{t('home.tickets')}: {me.currencies.tickets}</PixelText>
        <PixelText size="sm" color="pink">{t('home.lp')}: {me.ladderPts}</PixelText>
      </div>

      {/* Sprite strip */}
      <div className="flex gap-4 justify-center relative">
        {TUTORIAL_SPRITE_IDS.map(id => (
          <img
            key={id}
            src={`/stanwars/sprites/members/${id}/portrait.png`}
            alt={id}
            className="opacity-90"
            style={{ width: 96, height: 96, imageRendering: 'pixelated', objectFit: 'contain' }}
          />
        ))}
      </div>

      {/* Nav cards — 3-col on desktop, 2-col on mobile */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full max-w-2xl mt-2 relative">
        {NAV_CARDS.map(({ icon, labelKey, to, tone }) => (
          <Link
            key={to}
            to={to}
            className={to === '/settings' ? 'col-span-2 md:col-span-3' : ''}
          >
            <PixelButton variant="card" tone={tone} className="w-full text-center">
              {icon} {t(labelKey)}
            </PixelButton>
          </Link>
        ))}
      </div>
    </main>
  );
}

function MarketingView() {
  const { t } = useTranslation();
  const audio = useAudio();
  const backendAvailable = usePlayerStore(s => s.backendAvailable);
  useEffect(() => {
    const handler = () => { audio.enable(); window.removeEventListener('pointerdown', handler); };
    window.addEventListener('pointerdown', handler, { once: true });
    return () => window.removeEventListener('pointerdown', handler);
  }, [audio]);

  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-8 p-6 relative overflow-hidden">
      {/* Backdrop */}
      <img
        src="/stanwars/sprites/groups/bts.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none"
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          objectFit: 'fill',
          imageRendering: 'pixelated',
          opacity: 0.22,
        }}
      />
      {/* Sprite strip */}
      <div className="flex gap-4 justify-center relative">
        {TUTORIAL_SPRITE_IDS.map(id => (
          <img
            key={id}
            src={`/stanwars/sprites/members/${id}/portrait.png`}
            alt={id}
            className="opacity-90"
            style={{ width: 96, height: 96, imageRendering: 'pixelated', objectFit: 'contain' }}
          />
        ))}
      </div>
      <PixelText size="3xl" color="pink">{t('home.title')}</PixelText>
      <PixelText size="base" color="cyan">{t('home.tagline')}</PixelText>
      <div className="flex flex-col gap-3 mt-4 relative">
        <Link to="/exhibition"><PixelButton>{t('home.exhibition')} ▶</PixelButton></Link>
        {backendAvailable
          ? <Link to="/login"><PixelButton variant="ghost">{t('home.sign_in')}</PixelButton></Link>
          : <PixelText size="sm" color="yellow" className="opacity-60 text-center">{t('home.sign_in_soon')}</PixelText>
        }
      </div>
      <PixelText size="sm" color="yellow" className="mt-12 opacity-60 relative">
        {t('home.version')} v0.1.0
      </PixelText>
    </main>
  );
}

export function Home() {
  const me = usePlayerStore(s => s.me);
  return me ? <SignedInDashboard /> : <MarketingView />;
}
